import * as React from 'react';
import MDialog from '../../../components/controls/MDialog';
import { Button, Typography, Stack, TextField, Grid, FormControlLabel, Switch, InputAdornment } from '@mui/material';
import { Confirm, useNotify } from 'react-admin';
import MQueryBuilder from '../../../components/query-builder/MQueryBuilder';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import { Delete, Send } from '@mui/icons-material';
import { defaultExcludeForString } from '../../../components/query-builder/querybuilder.config';
import MNumber from '../../../components/controls/MNumber';
import MTextField from '../../../components/controls/MTextField';
import MCheckbox from '../../../components/controls/MCheckbox';
import MSelect from '../../../components/controls/MSelect';
import type { IData } from '../../../types';
import { MLabel } from '../../../components/controls/MLabel';

const emptyError = 'This field cannot be empty';
const PREFIX_RULE = 'rule://?rule=';
const PREFIX_MAILTO = 'mailto:';
const DEFAULT_FORWARD_TO = 'order_support@tickethq.com';

interface RulePopupProps {
    dataProvider: IData;
    data: { isOpenRule: boolean; rowSelected: IData | null };
    reloadPage: () => void;
    onClose: () => void;
    ruleTypeList: IData[];
}

const RulePopup = ({ dataProvider, data, reloadPage, onClose, ruleTypeList }: RulePopupProps) => {
    const { isOpenRule, rowSelected } = data;
    const actionQuery = React.useRef<any>(null);
    const conditionQuery = React.useRef<any>(null);
    const [editData, setEditData] = React.useState<IData>();
    const [forwardTo, setForwardTo] = React.useState({ checked: true, value: DEFAULT_FORWARD_TO });
    const [isMailto, setIsMailto] = React.useState(true);
    const [isAdvance, setIsAdvance] = React.useState(false);
    const notify = useNotify();

    const [state, setState] = React.useState({
        valids: [] as IData[],
        isLoading: true,
        isLoadingForSave: false,
        isLoadingForDelete: false,
        // for confirm
        isConfirmSave: false,
        isConfirmDelete: false,
    });

    const conditionInitData = {
        combinator: 'AND',
        rules: []
    };
    const actionInitData = {
        combinator: 'modify',
        rules: []
    };

    const combinators = [
        { name: 'modify', label: '' }
    ];

    const parseData = (rule: IData) => {
        if (rule.id && rule.forwardTo.startsWith(PREFIX_RULE)) {
            let forwardTo = rule.forwardTo.replace(PREFIX_RULE, '');
            let parsed = decodeURIComponent(forwardTo);
            let parsedRule = JSON.parse(parsed);
            rule.condition = parsedRule.conditions;
            rule.action = parsedRule.actions.find((x: IData) => x.method === 'modify');
            let forwardToObj = parsedRule.actions.find((x: IData) => x.method === 'forwardTo');
            if (forwardToObj) {
                let ftValue = forwardToObj.rules[0].value;
                setForwardTo((prev) => ({ ...prev, checked: !!ftValue, value: ftValue }));
            }
            setIsAdvance(true);
        }
        return rule;
    };

    React.useEffect(() => {
        setState((prev) => ({ ...prev, isLoading: false }));
        if (rowSelected) {
            let parsed = parseData(Object.assign({}, rowSelected));
            if (parsed.id && parsed.forwardTo.startsWith(PREFIX_MAILTO)) {
                setIsMailto(true);
                parsed.forwardTo = parsed.forwardTo.replace(PREFIX_MAILTO, '');
            } else if (!parsed.id) setIsMailto(true);
            else setIsMailto(false);
            setEditData(parsed);
        }
    }, [rowSelected]);

    const conditionFields = [
        { id: 'subject', name: 'subject', label: 'Subject', input: 'text', type: 'string', excludes: defaultExcludeForString },
        { id: 'body', name: 'body', label: 'Body', input: 'text', type: 'string', excludes: defaultExcludeForString },
        { id: 'from', name: 'from', label: 'From', input: 'text', type: 'string', excludes: defaultExcludeForString },
        { id: 'to', name: 'to', label: 'To', input: 'text', type: 'string', excludes: defaultExcludeForString }
    ];

    const actionFields = [
        { id: 'tags', name: 'tags', label: 'Tags', input: 'text', type: 'string', operators: [{ name: 'push', label: 'push' }], placeholder: '' },
    ];

    const isError = (field: string) => state.valids.some((x: IData) => x[field] === true);


    const getQueryValues = () => {
        let condition = JSON.parse(conditionQuery.current.getValues('json'));
        let action = JSON.parse(actionQuery.current.getValues('json'));
        let _validateCondition = conditionQuery.current.getValidate(true);
        let _validateAction = actionQuery.current.getValidate(true);
        if (!_validateCondition) notify('Invalid conditions', { type: 'error' });
        if (!_validateAction) notify('Invalid actions', { type: 'error' });
        if (!_validateAction || !_validateCondition) {
            return null;
        }
        return { condition, action };
    };

    const onDelete = () => {
        const ed = editData as IData;
        setState((prev) => ({ ...prev, isLoadingForDelete: true }));
        dataProvider.delete('Email', `DeleteEmailRule?id=${ed.id}`)
            .then(function () {
                setState((prev) => ({ ...prev, isLoadingForDelete: false }));
                notify('Email rule was deleted', { type: 'success' });
                reloadPage();
                onClose();
            }).catch((error: IData) => {
                notify(`Delete has error: ${error}`, { type: 'error' });
                setState((prev) => ({ ...prev, isLoadingForDelete: false }));
            });
    };

    const onSave = () => {
        let isNullName = !(editData as IData).description?.trim();
        setState((prev) => ({ ...prev, valids: [{ name: isNullName }] }));
        if (isNullName) return;
        if (isAdvance) {
            if (!getQueryValues()) return;
            if (forwardTo.checked && !forwardTo.value) return;
            if (forwardTo.checked && !forwardTo.value.trim()) {
                setForwardTo((prev) => ({ ...prev, value: forwardTo.value.trim() }));
                return;
            }
        }
        setState((prev) => ({ ...prev, isConfirmSave: true }));
    };

    const handleSave = () => {
        const ed = editData as IData;
        setState((prev) => ({ ...prev, isLoadingForSave: true }));
        let forwardToObj = null;
        if (isAdvance) {
            let queryValues = getQueryValues();
            if (!queryValues) return;

            const ACTION_FORWARD_TO = { 'method': 'forwardTo', 'rules': [{ 'value': forwardTo.checked ? forwardTo.value.trim() : '' }] };
            let { condition, action } = queryValues;
            forwardToObj = {
                conditions: condition,
                actions: action ? [action, ACTION_FORWARD_TO] : [ACTION_FORWARD_TO]
            } as any;
        }
        let newForwardTo;
        if (forwardToObj) newForwardTo = PREFIX_RULE + encodeURIComponent(JSON.stringify(forwardToObj));
        else {
            newForwardTo = ed.forwardTo || '';
            if (isMailto && !newForwardTo.startsWith(PREFIX_MAILTO) || !isMailto && ed.forwardTo === DEFAULT_FORWARD_TO) {
                newForwardTo = PREFIX_MAILTO + newForwardTo;
            }
        }

        let data = {
            id: ed.id,
            MailBox: ed.mailBox,
            Description: ed.description,
            Subject: ed.subject,
            From: ed.from,
            To: ed.to,
            Body: ed.body,
            Active: ed.active,
            Priority: ed.priority,
            RuleType: ed.ruleType,
            Final: 0,
            ContinueOnError: 0,
            ForwardTo: newForwardTo
        };

        dataProvider.postData('Email', 'UpdateEmailRule', data)
            .then(function (response: IData) {
                setState((prev) => ({ ...prev, isLoadingForSave: false }));
                if (response.data) {
                    notify('Save successful', { type: 'success' });
                    reloadPage();
                    onClose();
                } else {
                    notify('Failed to save email rule', { type: 'error' });
                }
                if (response.toString()?.includes('HttpError')) notify(`Error: ${response.toString()}`, { type: 'error' });
            }).catch((error: IData) => {
                setState((prev) => ({ ...prev, isLoadingForSave: false }));
                notify(`Error: ${error}`, { type: 'error' });
            });
    };

    return (
        <>
            {editData &&
                <MDialog
                    name={'dlg-email-rule'}
                    open={isOpenRule}
                    title={editData.id > 0 ? `#${editData.id}` : 'New email rule'}
                    handleClose={onClose}
                    maxWidth="lg"
                    msgLoading={state.isLoadingForSave ? 'Saving...' : state.isLoadingForDelete ? 'Deleting...' : 'Loading...'}
                    disabled={state.isLoadingForSave || state.isLoadingForDelete}
                    action={
                        <Stack width='100%' direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
                            <MCheckbox
                                label={'Active'}
                                defaultValue={editData.active}
                                onChange={(checked: boolean | null) => setEditData((prev) => ({ ...prev, active: checked }))} />
                            <Stack direction="row" spacing={1} justifyContent={'flex-end'}>
                                <Button disabled={state.isLoadingForSave} startIcon={<Send />} variant="contained" color='info' onClick={onSave}>Save</Button>
                                {editData.id > 0 && <Button startIcon={<Delete />} color={'error'} variant='contained' onClick={() => setState((prev) => ({ ...prev, isConfirmDelete: true }))}>Delete</Button>}
                            </Stack>
                        </Stack>
                    }
                >
                    <TextField
                        value={editData.description}
                        error={isError('name')}
                        helperText={isError('name') ? emptyError : null}
                        fullWidth
                        required
                        label="Description"
                        onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))}
                        variant="outlined" />
                    <Grid container spacing={2}>
                        <Grid size={8}>
                            <TextField
                                value={editData.mailBox}
                                fullWidth
                                label="Mail Box"
                                onChange={(e) => setEditData((prev) => ({ ...prev, mailBox: e.target.value }))}
                                variant="outlined" />
                        </Grid>
                        <Grid size={4}>
                            <MSelect
                                label={'Rule Type'}
                                variant="outlined"
                                defaultValue={editData.ruleType}
                                choices={ruleTypeList as any[]}
                                isEmptyOption={false}
                                onChange={(value: any) => setEditData((prev) => ({ ...prev, ruleType: value }))} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid size={8}>
                            <TextField
                                value={editData.subject}
                                fullWidth
                                label="Subject"
                                onChange={(e) => setEditData((prev) => ({ ...prev, subject: e.target.value }))}
                                variant="outlined" />
                        </Grid>
                        <Grid size={4}>
                            <MNumber
                                variant="outlined"
                                label="Priority"
                                value={editData.priority || 0}
                                onChange={(e: any) => setEditData((prev) => ({ ...prev, priority: e }))} />
                        </Grid>
                    </Grid>

                    {/* For basic */}
                    {!isAdvance && (
                        <>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <MTextField
                                        fullWidth
                                        label="From"
                                        defaultValue={editData.from}
                                        onChange={(e: string) => setEditData((prev) => ({ ...prev, from: e }))}
                                        variant="outlined" />
                                </Grid>
                                <Grid size={6}>
                                    <MTextField
                                        fullWidth
                                        label="To"
                                        defaultValue={editData.to}
                                        onChange={(e: string) => setEditData((prev) => ({ ...prev, to: e }))}
                                        variant="outlined" />
                                </Grid>
                            </Grid>
                            <MTextField
                                fullWidth
                                label="Body"
                                defaultValue={editData.body}
                                onChange={(e: string) => setEditData((prev) => ({ ...prev, body: e }))}
                                variant="outlined" />
                        </>
                    )}

                    {!isAdvance && (
                        <MCheckbox
                            label={'Mail to'}
                            defaultValue={isMailto}
                            onChange={(checked: any) => setIsMailto(checked)}
                        />
                    )}
                    <TextField
                        fullWidth
                        multiline
                        minRows={isAdvance ? 2 : 1}
                        maxRows={isAdvance ? 2 : 1}
                        disabled={isAdvance}
                        value={editData.forwardTo}
                        label="Forward To"
                        placeholder='Enter an email'
                        onChange={(e) => setEditData((prev) => ({ ...prev, forwardTo: e.target.value }))}
                        variant="outlined"
                        slotProps={{ input: { startAdornment: !isAdvance && isMailto && <InputAdornment position='start'>mailto:</InputAdornment> } }}
                    />

                    <FormControlLabel control={<Switch defaultChecked={isAdvance} checked={isAdvance} onChange={(e) => setIsAdvance(e.target.checked)} />} label="Advance" />

                    {isAdvance && (
                        <>
                            <Typography variant="h6" gutterBottom flex="1" key='math'>
                                For those orders match ...
                            </Typography>
                            <QueryBuilderMaterial>
                                <MQueryBuilder
                                    key='condition'
                                    fields={conditionFields}
                                    initialData={conditionInitData}
                                    ref={conditionQuery}
                                    editData={editData.condition}
                                    defaultOperator='and'
                                    hideNotButton />

                                <MLabel key={'adjust'} name={'Then adjust'} h={'h6'} />

                                <Stack width='100%' direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <MCheckbox
                                        label={'Forward to'}
                                        defaultValue={forwardTo.checked}
                                        onChange={(checked: any) => setForwardTo((prev) => ({ ...prev, checked }))} />
                                    <MTextField
                                        sx={{ maxWidth: 500 }}
                                        required={forwardTo.checked}
                                        error={forwardTo.checked && !forwardTo.value}
                                        helperText={forwardTo.checked && !forwardTo.value ? emptyError : null}
                                        defaultValue={forwardTo.value}
                                        variant="outlined"
                                        disabled={!forwardTo.checked}
                                        onChange={(value: string) => setForwardTo((prev) => ({ ...prev, value }))}
                                    />
                                </Stack>

                                <MQueryBuilder
                                    key='action'
                                    fields={actionFields}
                                    initialData={actionInitData}
                                    ref={actionQuery}
                                    editData={editData.action}
                                    defaultOperator='modify'
                                    hideGroupButton
                                    hideLockButtons
                                    hideNotButton
                                    combinators={combinators}
                                    combinatorName='method'
                                />
                            </QueryBuilderMaterial>
                        </>
                    )}

                </MDialog>
            }

            <Confirm isOpen={state.isConfirmSave}
                title='Save rule'
                content='Are you sure you want to save this rule?'
                onClose={() => setState((prev) => ({ ...prev, isConfirmSave: false }))}
                onConfirm={() => {
                    handleSave();
                    setState((prev) => ({ ...prev, isConfirmSave: false }));
                }} />
            <Confirm isOpen={state.isConfirmDelete}
                title='Delete rule'
                content='Are you sure you want to delete this rule?'
                onClose={() => setState((prev) => ({ ...prev, isConfirmDelete: false }))}
                onConfirm={() => {
                    onDelete();
                    setState((prev) => ({ ...prev, isConfirmDelete: false }));
                }} />
        </>
    );
};

export default RulePopup;
